/**
 * @file _DrawerComponent.ts
 * @module _admin/assets/ts/components/_DrawerComponent
 * @description Implements the  DrawerComponent interactive admin-shell component, including initialization, state management, and DOM event handling.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 1.3.0
 */

import {
  EventHandlerUtil,
  getUniqueIdWithPrefix,
  getObjectPropertyValueByKey,
  stringSnakeToCamel,
  getAttributeValueByBreakpoint,
  throttle,
  getCSS,
  DOMEventHandlerUtil,
  ElementStyleUtil,
} from "../_utils/index";

/**
 * Global store for managing initialized DrawerComponent instances.
 *
 * @class
 */
export class DrawerStore {
  static store: Map<string, DrawerComponent> = new Map();

  /**
   * Registers a drawer component instance in the store under a unique key.
   *
   * Checks if an entry already exists for the key before storing to prevent overwriting.
   *
   * @param {string} instanceId - Unique identifier for the drawer instance.
   * @param {DrawerComponent} drawerComponentObj - The drawer component instance to register.
   * @returns {void}
   * @static
   */
  public static set(
    instanceId: string,
    drawerComponentObj: DrawerComponent
  ): void {
    if (DrawerStore.has(instanceId)) {
      return;
    }

    DrawerStore.store.set(instanceId, drawerComponentObj);
  }

  /**
   * Retrieves a drawer component instance from the store by its identifier.
   *
   * @param {string} instanceId - Unique identifier of the drawer component to retrieve.
   * @returns {DrawerComponent|undefined} The registered drawer component, or undefined if not found.
   * @static
   */
  public static get(instanceId: string): DrawerComponent | undefined {
    if (!DrawerStore.has(instanceId)) {
      return;
    }
    return DrawerStore.store.get(instanceId);
  }

  /**
   * Removes a drawer component instance from the store.
   *
   * @param {string} instanceId - Unique identifier of the drawer component to remove.
   * @returns {void}
   * @static
   */
  public static remove(instanceId: string): void {
    if (!DrawerStore.has(instanceId)) {
      return;
    }

    DrawerStore.store.delete(instanceId);
  }

  /**
   * Checks whether a drawer component instance is registered under the given identifier.
   *
   * @param {string} instanceId - Unique identifier to check in the store.
   * @returns {boolean} True if the instance exists in the store, false otherwise.
   * @static
   */
  public static has(instanceId: string): boolean {
    return DrawerStore.store.has(instanceId);
  }

  /**
   * Returns the complete map of registered drawer component instances.
   *
   * @returns {object} Map of instance IDs to DrawerComponent instances.
   * @static
   */
  public static getAllInstances() {
    return DrawerStore.store;
  }
}

export interface DrawerOptions {
  overlay: boolean;
  baseClass: string;
  overlayClass: string;
  direction: string;
}

const defaultDrawerOptions: DrawerOptions = {
  overlay: true,
  baseClass: "drawer",
  overlayClass: "drawer-overlay",
  direction: "end",
};

/**
 * UI drawer component for managing off-canvas side panels and overlay drawers.
 *
 * Handles DOM toggles, backdrop overlay creation, dynamic positioning, width styling,
 * responsive breakpoint options, and lifecycle events.
 *
 * @class
 */
class DrawerComponent {
  element: HTMLElement;
  overlayElement: HTMLElement | null = null;
  toggleElement: HTMLElement | null = null;
  options: DrawerOptions;
  instanceUid: string;
  name: string = "";
  shown: boolean = false;
  lastWidth: number = 0;
  closeElement: HTMLElement | null = null;

  /**
   * Creates an instance of DrawerComponent and registers it in the drawer store.
   *
   * Merges default options with user configuration, initializes event listener bindings,
   * applies layout updates, and registers the instance with DrawerStore using the element's ID.
   *
   * @param {HTMLElement} _element - The target container element for the drawer.
   * @param {object} options - Configuration options for drawer behavior and styling.
   */
  constructor(_element: HTMLElement, options: DrawerOptions) {
    this.element = _element;
    this.options = Object.assign(defaultDrawerOptions, options);
    this.instanceUid = getUniqueIdWithPrefix("drawer");
    this.overlayElement = null;
    this.name = this.element.getAttribute("data-kt-drawer-name") || "";
    this.shown = false;
    this.toggleElement = null;
    // Event Handlers
    this._handlers();
    // Update Instance
    this._update();
    // Bind Instance
    DrawerStore.set(this.element.id, this);
  }

  /**
   * Binds click event handlers on the document body for configured toggle and close selectors.
   *
   * Intercepts click events to toggle or hide the drawer when trigger elements are clicked.
   */
  private _handlers = () => {
    const togglers = this._getOption("toggle") as string;
    const closers = this._getOption("close") as string;

    if (togglers !== null && togglers.length > 0) {
      DOMEventHandlerUtil.on(document.body, togglers, "click", (e: Event) => {
        e.preventDefault();
        this.toggleElement = document.getElementById(togglers);
        this._toggle();
      });
    }

    if (closers !== null && closers.length > 0) {
      DOMEventHandlerUtil.on(document.body, closers, "click", (e: Event) => {
        e.preventDefault();
        this.closeElement = document.getElementById(closers);

        this._hide();
      });
    }
  };

  /**
   * Updates the drawer component layout state, dimensions, and active styling classes.
   *
   * Evaluates configured options such as activation state, direction, and width, resetting
   * or applying DOM attributes and styles accordingly.
   */
  private _update = () => {
    const width = String(this._getOption("width"));
    const direction = String(this._getOption("direction"));

    // Reset state
    const hasBaseClass = this.element.classList.contains(
      `${this.options.baseClass}-on`
    );
    const bodyCanvasAttr = String(
      document.body.getAttribute(`data-kt-drawer-${this.name}-`)
    );

    if (hasBaseClass === true && bodyCanvasAttr === "on") {
      this.shown = true;
    } else {
      this.shown = false;
    }

    // Activate/deactivate
    if (this._getOption("activate") === true) {
      this.element.classList.add(this.options.baseClass);
      this.element.classList.add(`${this.options.baseClass}-${direction}`);
      ElementStyleUtil.set(this.element, "width", width, true);

      this.lastWidth = parseInt(width);
    } else {
      ElementStyleUtil.set(this.element, "width", "");
      this.element.classList.remove(this.options.baseClass);
      this.element.classList.remove(`${this.options.baseClass}-${direction}`);
      this._hide();
    }
  };

  /**
   * Retrieves a configuration option value from element data attributes or component options.
   *
   * Checks for element data attributes matching the option name (handling responsive breakpoints)
   * before falling back to camelCase properties on the options object.
   *
   * @param {string} name - Name of the option to resolve.
   * @returns {object|string|boolean|null} Resolved option value, boolean, or null if unconfigured.
   */
  private _getOption = (name: string) => {
    const attr = this.element.getAttribute(`data-kt-drawer-${name}`);
    if (attr) {
      let value = getAttributeValueByBreakpoint(attr);
      if (value !== null && String(value) === "true") {
        return true;
      } else {
        if (value !== null && String(value) === "false") {
          return false;
        }
      }

      return value;
    } else {
      const optionName = stringSnakeToCamel(name);
      const option = getObjectPropertyValueByKey(this.options, optionName);
      if (option) {
        return getAttributeValueByBreakpoint(option);
      } else {
        return null;
      }
    }
  };

  /**
   * Toggles the drawer between visible and hidden states.
   *
   * Triggers pre-toggle and post-toggle events (`kt.drawer.toggle` and `kt.drawer.toggled`),
   * aborting the operation if the pre-toggle event is cancelled.
   */
  private _toggle = () => {
    if (EventHandlerUtil.trigger(this.element, "kt.drawer.toggle") === false) {
      return;
    }

    if (this.shown) {
      this._hide();
    } else {
      this._show();
    }

    EventHandlerUtil.trigger(this.element, "kt.drawer.toggled");
  };

  /**
   * Hides the active drawer component and updates state attributes.
   *
   * Cancels execution if the `kt.drawer.hide` event is prevented, then removes active classes,
   * deletes backdrop overlays, cleans up body data attributes, and fires `kt.drawer.after.hidden`.
   */
  private _hide = () => {
    if (EventHandlerUtil.trigger(this.element, "kt.drawer.hide") === false) {
      return;
    }

    this.shown = false;
    this._deleteOverlay();
    document.body.removeAttribute(`data-kt-drawer-${this.name}`);
    document.body.removeAttribute(`data-kt-drawer`);
    this.element.classList.remove(`${this.options.baseClass}-on`);
    if (this.toggleElement != null) {
      this.toggleElement.classList.remove("active");
    }

    EventHandlerUtil.trigger(this.element, "kt.drawer.after.hidden");
  };

  /**
   * Displays the drawer component and updates relevant DOM attributes.
   *
   * Cancels execution if the `kt.drawer.show` event is prevented, then updates visibility flags,
   * adds active styling classes, creates a backdrop overlay if configured, and fires `kt.drawer.shown`.
   */
  private _show = () => {
    if (EventHandlerUtil.trigger(this.element, "kt.drawer.show") === false) {
      return;
    }

    this.shown = true;
    this._createOverlay();
    document.body.setAttribute(`data-kt-drawer-${this.name}`, "on");
    document.body.setAttribute("data-kt-drawer", "on");
    this.element.classList.add(`${this.options.baseClass}-on`);
    if (this.toggleElement !== null) {
      this.toggleElement.classList.add("active");
    }

    EventHandlerUtil.trigger(this.element, "kt.drawer.shown");
  };

  /**
   * Creates and appends a backdrop overlay element to the document body if overlay mode is enabled.
   *
   * Computes z-index offset relative to the drawer element, attaches optional CSS classes,
   * and sets up a click event handler on the overlay to trigger drawer hiding.
   */
  private _createOverlay = () => {
    if (this._getOption("overlay") === true) {
      this.overlayElement = document.createElement("DIV");
      const elementZIndex = getCSS(this.element, "z-index");
      if (elementZIndex) {
        const overlayZindex = parseInt(elementZIndex) - 1;
        ElementStyleUtil.set(this.overlayElement, "z-index", overlayZindex); // update
      }
      document.body.append(this.overlayElement);
      const overlayClassOption = this._getOption("overlay-class");
      if (overlayClassOption) {
        this.overlayElement.classList.add(overlayClassOption.toString());
      }
      this.overlayElement.addEventListener("click", (e) => {
        e.preventDefault();
        this._hide();
      });
    }
  };

  /**
   * Removes the drawer backdrop overlay element from the DOM tree.
   *
   * Checks if the overlay element exists and has a parent node before removing it from DOM.
   */
  private _deleteOverlay = () => {
    if (this.overlayElement !== null && this.overlayElement.parentNode) {
      this.overlayElement.parentNode.removeChild(this.overlayElement);
    }
  };

  /**
   * Gets the drawer placement direction.
   *
   * @returns {string} Returns "left" if configured, otherwise defaults to "right".
   */
  private _getDirection = () => {
    return String(this._getOption("direction")) === "left" ? "left" : "right";
  };

  /**
   * Retrieves the drawer width option or computes it if set to auto.
   *
   * @returns {string|number|object} Configured width string/number or computed CSS width value.
   */
  private _getWidth = () => {
    let width = this._getOption("width");
    if (width && width === "auto") {
      width = getCSS(this.element, "width");
    }

    return width;
  };

  ///////////////////////
  // ** Public API  ** //
  ///////////////////////
  /**
   * Toggles the drawer visibility state between open and closed.
   */
  public toggle = () => {
    this._toggle();
  };

  /**
   * Displays the drawer component and renders its overlay backdrop.
   */
  public show = () => {
    this._show();
  };

  /**
   * Hides the drawer component and cleans up its overlay backdrop element.
   */
  public hide = () => {
    this._hide();
  };

  /**
   * Determines whether the drawer is currently visible.
   *
   * @returns {boolean} True if the drawer is shown, false otherwise.
   */
  public isShown = () => {
    return this.shown;
  };

  /**
   * Recalculates and updates the drawer's UI options and dimensions.
   */
  public update = () => {
    this._update();
  };

  /**
   * Returns the root DOM element of the drawer component.
   *
   * @returns {object} The drawer root DOM element reference.
   */
  public goElement = () => {
    return this.element;
  };

  // Event API
  /**
   * Binds an event handler to the drawer root element.
   *
   * @param {string} name - Event name.
   * @param {Function} handler - Callback function invoked on event.
   * @returns {string} Unique handler identifier.
   */
  public on = (name: string, handler: Function) => {
    return EventHandlerUtil.on(this.element, name, handler);
  };

  /**
   * Binds a one-time event handler to the drawer root element.
   *
   * @param {string} name - Event name.
   * @param {Function} handler - Callback function invoked once on event trigger.
   * @returns {string} Unique handler identifier.
   */
  public one = (name: string, handler: Function) => {
    return EventHandlerUtil.one(this.element, name, handler);
  };

  /**
   * Unbinds an event handler from the drawer root element by handler identifier.
   *
   * @param {string} name - Event name.
   * @param {string} handerId - Registered handler identifier to detach.
   * @returns {void}
   */
  public off = (name: string, handerId: string) => {
    return EventHandlerUtil.off(this.element, name, handerId);
  };

  /**
   * Triggers a custom event on the drawer root element.
   *
   * @param {string} name - Name of the event to dispatch.
   * @param {Event} event - Event object payload.
   * @returns {boolean} Result of the event trigger.
   */
  public trigger = (name: string, event: Event) => {
    return EventHandlerUtil.trigger(this.element, name, event);
  };

  // Static methods
  /**
   * Checks whether a drawer instance exists in the global store for a given element ID.
   *
   * @param {string} elementId - Unique identifier of the target DOM element.
   * @returns {boolean} True if an instance is registered in DrawerStore, false otherwise.
   */
  public static hasInstace = (elementId: string): boolean => {
    return DrawerStore.has(elementId);
  };

  /**
   * Retrieves a registered drawer instance from the global store.
   *
   * @param {string} elementId - Unique identifier of the target DOM element.
   * @returns {object} The drawer component instance, or undefined if not found.
   */
  public static getInstance = (elementId: string) => {
    return DrawerStore.get(elementId);
  };

  /**
   * Hides all active drawer instances tracked in the store.
   *
   * Retrieves all registered drawer component instances from {@link DrawerStore}
   * and invokes their `hide` method to close them.
   *
   * @static
   */
  public static hideAll = () => {
    const oldInstances = DrawerStore.getAllInstances();
    oldInstances.forEach((dr) => {
      dr.hide();
    });
  };

  /**
   * Updates all active drawer instances tracked in the store.
   *
   * Retrieves all registered drawer component instances from {@link DrawerStore}
   * and invokes their `update` method to recalculate layout and positions.
   *
   * @static
   */
  public static updateAll = () => {
    const oldInstances = DrawerStore.getAllInstances();
    oldInstances.forEach((dr) => {
      dr.update();
    });
  };

  // Create Instances
  /**
   * Instantiates drawer components for DOM elements matching the specified selector.
   *
   * Queries the DOM for elements matching `selector`. For each element, checks if an
   * existing instance exists by ID. If not, instantiates a new `DrawerComponent` with
   * default options, updates its element reference, and ensures it is initially hidden.
   *
   * @param {string} selector - CSS selector used to match target drawer elements.
   * @static
   */
  public static createInstances(selector: string): void {
    const elements = document.body.querySelectorAll(selector);
    elements.forEach((element) => {
      const item = element as HTMLElement;
      let drawer = DrawerComponent.getInstance(item.id);
      if (!drawer) {
        drawer = new DrawerComponent(item, defaultDrawerOptions);
      }
      drawer.element = item;
      drawer.hide();
    });
  }

  // Dismiss instances
  /**
   * Binds click listeners to handle drawer dismiss action triggers.
   *
   * Listens for click events on elements matching `[data-kt-drawer-dismiss="true"]` within
   * `document.body`. Identifies the parent drawer element, retrieves its component instance,
   * and hides it if currently visible.
   *
   * @static
   */
  public static handleDismiss = () => {
    // External drawer toggle handler
    DOMEventHandlerUtil.on(
      document.body,
      '[data-kt-drawer-dismiss="true"]',
      "click",
      () => {
        /* @ts-ignore */
        const element = this.closest('[data-kt-drawer="true"]');
        if (element) {
          const drawer = DrawerComponent.getInstance(element);
          if (drawer && drawer.isShown()) {
            drawer.hide();
          }
        }
      }
    );
  };

  // Global Initialization
  /**
   * Registers global event handlers for drawer responsiveness.
   *
   * Attaches a throttled listener to the window `resize` event. On resize, locates all
   * drawer elements in the DOM and triggers an `update` call on their respective instances.
   *
   * @static
   */
  public static initGlobalHandlers(): void {
    // Window Resize Handling
    window.addEventListener("resize", function () {
      let timer: number | undefined;
      throttle(
        timer,
        () => {
          // Locate and update Drawer instances on window resize
          const elements = document.body.querySelectorAll(
            '[data-kt-drawer="true"]'
          );
          elements.forEach((el) => {
            const item = el as HTMLElement;
            const instance = DrawerComponent.getInstance(item.id);
            if (instance) {
              instance.element = item;
              instance.update();
            }
          });
        },
        200
      );
    });
  }

  /**
   * Initializes all drawer components and global event handlers on application setup.
   *
   * Creates drawer instances for elements matching `[data-kt-drawer="true"]`, registers global
   * resize event listeners, and attaches dismiss click handlers.
   *
   * @static
   */
  public static bootstrap = () => {
    DrawerComponent.createInstances('[data-kt-drawer="true"]');
    DrawerComponent.initGlobalHandlers();
    DrawerComponent.handleDismiss();
  };

  /**
   * Reinitializes drawer components across the page.
   *
   * Re-creates instances for `[data-kt-drawer="true"]` elements, hides and updates all
   * existing drawer instances, and re-attaches dismiss event handlers.
   *
   * @static
   */
  public static reinitialization = () => {
    DrawerComponent.createInstances('[data-kt-drawer="true"]');
    DrawerComponent.hideAll();
    DrawerComponent.updateAll();
    DrawerComponent.handleDismiss();
  };
}

export { DrawerComponent, defaultDrawerOptions };
